/* Core & libraries */
import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { ProgressBar } from "react-bootstrap";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { usePeopleService } from "../../../services/peopleService";
import { useLoyaltyService } from "../../../services/loyaltyService";
import { nameInitials } from "../../../helpers/general";

/* Components */
import UCButton from "../../../components/buttons";
import UCGrid from "../../ucgrid";
import UCAutocomplete from "../../ucautocomplete";
import { ReactComponent as EmptyGridLogo } from "../../../resources/images/assign-individually-empty-lep.svg";
import { ReactComponent as EditGridLogo } from "../../../resources/images/assign-individually-edit-lep.svg";
import { ReactComponent as TrashRedIcon } from "../../../resources/images/trash-red-icon.svg";
import ChooseTierModal from "../../modals/chooseTierModal";
import TierRequiredModal from "../../modals/tierRequiredModal";
import ConfirmRedemptionModal from "../../modals/confirmRedemptionModal";

/* Styles */
import styles from "./styles";

const messages = defineMessages({
  searchPlaceholder: {
    id: "loyaltyAssignIndividually.searchPlaceholder",
  },
});

const AssignIndividually = forwardRef(({ initialBalance, setAssignModalShow, allotmentId, onRedeemCompleted, allowConciergeKey }, ref) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const intl = useIntl();

  const [options, setOptions] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showTierRequiredModal, setShowTierRequiredModal] = useState(false);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [activeModalUser, setActiveModalUser] = useState(null);
  const [selectedTierPoints, setSelectedTierPoints] = useState(0);
  const [progressValue, setProgressValue] = useState(100);
  const [pointDifference, setPointDifference] = useState(0);
  const [initialSelectedTier, setInitialSelectedTier] = useState(null);
  const [userPreviousTier, setUserPreviousTier] = useState(false);
  const [errorResponse, setErrorResponse] = useState(false);

  const { searchPeople } = usePeopleService();
  const { loyaltyTiersResponse, getTiersList, getUserTiersCheck } = useLoyaltyService();

  useEffect(() => {
    (async () => {
      await getTiersList();
    })();
  }, []);

  const convertedTiers = loyaltyTiersResponse?.loyaltyTierList?.map(
    (item) => ({
      ...item,
      points: +item.points,
    })
  );

  const [gridData, setGridData] = useState({
    results: {
      pageNumber: 1,
      pagesAvailable: 1,
      dataItems: [],
    },
    filters: [],
    sorters: [],
    defaultSort: {},
    columns: {
      avatarName: {
        fieldName: "avatarName",
        display: true,
        sortable: false,
        columnOrder: 1,
      },
      aadvantage: {
        fieldName: "aadvantage",
        display: true,
        sortable: false,
        columnOrder: 2,
      },
      awardTier: {
        fieldName: "awardTier",
        display: true,
        sortable: false,
        columnOrder: 3,
      },
      pointDeduction: {
        fieldName: "pointDeduction",
        display: true,
        sortable: false,
        columnOrder: 4,
      },
    },
    actions: [],
  });

  useImperativeHandle(ref, () => ({
    hasSelectedUsers: () => selectedUsers.length > 0,
  }));

  const getOptions = async (term) => {
    const response = await searchPeople(term, "ACTIVE");
    if (!response) {
      setErrorResponse(true);
      setOptions(null);
    } else {
      setErrorResponse(false);
      if (term.length < 3) {
        setOptions(null);
      } else {
        setOptions(response);
      }
    }
  };

  useEffect(() => {
    setGridData((prev) => ({
      ...prev,
      results: {
        ...prev.results,
        dataItems: selectedUsers,
      },
    }));
  }, [selectedUsers]);

  useEffect(() => {
    if (initialBalance > 0) {
      const usedPercentage = (selectedTierPoints / initialBalance) * 100;
      const remainingPercentage = 100 - usedPercentage;
      setProgressValue(remainingPercentage);
      setPointDifference(initialBalance - selectedTierPoints);
    }
  }, [selectedTierPoints]);

  let customProps = {};
  customProps.actionsComponent = (row) => (
    <div className={classes.deleteBtn} onClick={() => onSelectOption(row)}>
      <TrashRedIcon />
    </div>
  );

  useEffect(() => {
    if (!showTierModal) {
      setPointDifference(initialBalance - selectedTierPoints);
    }
  }, [showTierModal]);

  const handleChooseTier = async (row, isEditing) => {
    const response = await getUserTiersCheck(row.userEntityId);
    setUserPreviousTier(response)
    if (isEditing && row.pointDeduction) {
      setPointDifference((prev) => prev + row.pointDeduction);
    } else {
      setPointDifference(initialBalance - selectedTierPoints);
    }
    setActiveModalUser(row);
    setShowTierModal(true);
    const currentTierIndex = loyaltyTiersResponse?.loyaltyTierList?.findIndex(
      (tier) => tier.description === row.awardTier
    );
    setInitialSelectedTier(currentTierIndex >= 0 ? currentTierIndex : null);
  };

  const onSelectOption = (item) => {
    setSelectedUsers((prev) => {
      const index = prev.findIndex(
        (user) => user.userEntityId === item.userEntityId
      );
      if (index > -1) {
        const newTotalPoints = selectedTierPoints - prev[index].pointDeduction;
        setSelectedTierPoints(newTotalPoints);
        return prev.filter((_, i) => i !== index);
      } else {
        const fullUserData =
          options.find((option) => option.userEntityId === item.userEntityId) ||
          item;
        return [
          ...prev,
          {
            ...fullUserData,
            userEntityId: item.userEntityId,
            awardTier: "",
            pointDeduction: 0,
          },
        ];
      }
    });
  };

  const updateTier = (userId, tier, points, level) => {
    setSelectedUsers((prev) => {
      let newTotalPoints = selectedTierPoints;
      const currentUser = prev.find((user) => user.userEntityId === userId);
      if (currentUser) {
        if (currentUser.pointDeduction) {
          newTotalPoints -= currentUser.pointDeduction;
        }
      }
      newTotalPoints += points;
      setPointDifference(initialBalance - newTotalPoints);
      setSelectedTierPoints(newTotalPoints);
      return prev.map((user) =>
        user.userEntityId === userId
          ? {
              ...user,
              awardTier: tier,
              pointDeduction: points,
              tierLevel: level,
            }
          : user
      );
    });
    setShowTierModal(false);
  };

  const HoverableTierCell = ({ row, handleActionClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: "pointer" }}
      >
        <span
          className={classes.awardTierText}
          onClick={() => handleActionClick(row, true)}
        >
          {row.awardTier}
          {isHovered && (
            <div>
              <EditGridLogo />
            </div>
          )}
        </span>
      </div>
    );
  };

  const redeemPoints = () => {
    const unselectedTiers = selectedUsers.some(
      (item) => item.pointDeduction === 0
    );
    if (unselectedTiers) {
      setShowTierRequiredModal(true);
    } else {
      setShowRedemptionModal(true);
    }
  };

  return (
    <div className={classes.bodyWrapper}>
      <div className={classes.bodyContainer}>
        <div className={classes.assignHeader}>
          <div>
            <h1>
              <FormattedMessage id="loyaltyAssignIndividually.title" />
            </h1>
            <p>
              <FormattedMessage id="loyaltyAssignIndividually.subtitle" />
            </p>
          </div>
          <UCButton
            type="primary"
            onClick={redeemPoints}
            className={classes.redeemBtn}
            disabled={!selectedUsers.length}
          >
            <FormattedMessage id="loyaltyAssignIndividually.redeemBtn" />
          </UCButton>
        </div>
        <hr className={classes.divider} />
        <div className={classes.balanceContainer}>
          <div className={classes.assignSearchbox}>
            <h6>
              <FormattedMessage id="loyaltyAssignIndividually.searchInfo" />
            </h6>
            <UCAutocomplete
              type="ASSIGN_LEP"
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
              options={options}
              getOptions={getOptions}
              onSelectOption={onSelectOption}
              inputStyling={classes.inputAutocomplete}
              optionsStyling={classes.autocompleteOptions}
              assignSelected={selectedUsers}
              disabled={selectedUsers.length > 49}
              errorResponse={errorResponse}
              noResultsMessage = {<FormattedMessage id="loyaltyAssignIndividually.noResultsSearchPlaceholder" />}
            />
            {selectedUsers.length > 49 && (
              <p>
                <FormattedMessage id="loyaltyAssignIndividually.limitReached" />
              </p>
            )}
          </div>
          <div
            className={
              convertedTiers?.some(
                (item) => item.points && pointDifference >= item.points
              ) === false
                ? classes.balanceWrapperRed
                : classes.balanceWrapper
            }
          >
            <div className={classes.balanceText}>
              <p>
                <FormattedMessage id="loyaltyAssignIndividually.remainingBalance" />
              </p>
              <span>
                <FormattedMessage
                  id="loyaltyAssignIndividually.points"
                  values={{
                    points: pointDifference.toLocaleString(),
                  }}
                />
              </span>
            </div>
            <ProgressBar now={progressValue} />
          </div>
        </div>
        <div className={classes.assignGrid}>
          <UCGrid
            gridName="assignLoyaltyGrid"
            data={gridData?.results}
            columns={gridData?.columns}
            actions={gridData?.actions}
            customColumns={{
              avatarName: (row) => {
                const name = `${row?.firstName} ${row?.lastName}`;
                return (
                  <div className={classes.nameWrapper}>
                    <div className={classes.initialsAvatar}>
                      <span>{nameInitials(name)}</span>
                    </div>
                    <div className={classes.details}>
                      <p>{name}</p>
                      <p>{row?.emailAddress || "-"}</p>
                    </div>
                  </div>
                );
              },
              aadvantage: (row) => {
                return (
                  <div className={classes.advantagePointText}>
                    #{row?.membershipNumber?.replace(/\*/g, "•")}
                  </div>
                );
              },
              awardTier: (row) => {
                return row.awardTier ? (
                  <HoverableTierCell
                    row={row}
                    handleActionClick={handleChooseTier}
                  />
                ) : (
                  <UCButton
                    type="general"
                    onClick={() => handleChooseTier(row, false)}
                    className={classes.awardTierBtn}
                  >
                    <FormattedMessage id="loyaltyAssignIndividually.chooseTierBtn" />
                  </UCButton>
                );
              },
              pointDeduction: (row) => {
                return (
                  <div className={classes.advantagePointText}>
                    {row?.pointDeduction === 0
                      ? "-"
                      : row?.pointDeduction.toLocaleString()}
                  </div>
                );
              },
            }}
            columnCustomWidth={{
              avatarName: "300px",
              awardTier: "310px",
              aadvantage: "200px"
            }}
            columnCustomProps={[
              {
                columnName: "actions",
                allowOverflow: true,
                style: {
                  display: "flex",
                  justifyContent: "end",
                },
              },
            ]}
            {...customProps}
            onRowClick={() => {}}
            showHeader={true}
            noDataComponent={
              <div className={classes.emptyGridWrapper}>
                <div className={classes.logo}>
                  <EmptyGridLogo />
                </div>
                <h3>
                  <FormattedMessage id="loyaltyAssignIndividually.emptyTitle" />
                </h3>
                <p>
                  <FormattedMessage id="loyaltyAssignIndividually.emptySubtitle" />
                </p>
              </div>
            }
          />
        </div>
      </div>
      <ChooseTierModal
        showModal={showTierModal}
        setShowModal={setShowTierModal}
        updateTier={updateTier}
        activeUser={activeModalUser}
        pointDifference={pointDifference}
        initialSelectedTier={initialSelectedTier}
        tiersList={convertedTiers}
        userPreviousTier={userPreviousTier}
        allowConciergeKey={allowConciergeKey}
      />
      <TierRequiredModal
        showTierRequiredModal={showTierRequiredModal}
        setShowTierRequiredModal={setShowTierRequiredModal}
      />
      <ConfirmRedemptionModal
        showRedemptionModal={showRedemptionModal}
        setShowRedemptionModal={setShowRedemptionModal}
        pointsValue={selectedTierPoints}
        redeemData={selectedUsers}
        setAssignModalShow={setAssignModalShow}
        allotmentId={allotmentId}
        onRedeemCompleted={onRedeemCompleted}
      />
    </div>
  );
});

export default AssignIndividually;
